/*
 * Real customer orders on the Hero strip, Departures board, and Route
 * Network — polled from /pages/sat-live-status (a bare JSON page, no theme
 * layout — see templates/page.sat-live-status.liquid) every few seconds.
 * That page echoes three Shop metafields:
 *
 *   custom.recent_order_cities  a one-time backfill (list of "City, ST"
 *                               strings) seeded from real historical orders,
 *                               so a visitor's very first poll already shows
 *                               real cities instead of nothing.
 *   custom.last_order_city      the ongoing live signal, kept current by a
 *   custom.last_order_at        Shopify Flow ("Order Created" -> "Update
 *                               metafield") on every new order. last_order_at
 *                               just needs to be an increasing number (the
 *                               Flow setup uses Order > Number) so a quiet
 *                               store safely re-polls the same unchanged
 *                               value and does nothing.
 *
 * Wraps SATComponent.prototype.renderVals (same technique as the other sat-*
 * -patch.js files) to inject real order cities into three places, each
 * clearly labeled NEW ORDER / JUST IN so it's obvious which entries are real
 * vs. the fixed lore rows:
 *
 *   - Hero strip (FLIGHT / ROUTING / LOAD / STATUS) — live entries prepended,
 *     cycling the same flip-transform mechanic as the original 4.
 *   - Departures flipboard (boardRows) — one extra row, flipping through the
 *     live cities the same visual way the other 6 rows flip through theirs.
 *   - Route Network "Current Active Airways" (routeReadouts) — live entries
 *     prepended, with Mammoth Lakes, CA (SAT‑3472) GUARANTEED to always stay
 *     somewhere in the visible list — it's a fixed lore beat, not something
 *     live order volume should ever crowd out.
 *
 * The board's live row does NOT run its own timer any more — it used to
 * (an independent async loop sized to *approximate* the same cadence as the
 * 6 fixed rows), and two loops that only approximate the same speed drift
 * out of phase with each other: different start times, and any stall either
 * one hits shifts it independently of the other. That read as rows going
 * out of sync — some too early, others too late — instead of one wave.
 * Fixed by giving up this row's own clock entirely: SATLiveBoardBridge below
 * exposes just enough (how many live orders exist, and a setter for this
 * row's own step) for assets/sat-board-wave-patch.js's single wave loop to
 * drive it directly, on the exact same tick as the 6 fixed rows, as if it
 * were just one more row in that same array. See that file for the full
 * loop. This file still owns the live-order data itself (liveOrders below)
 * and the live row's rendering (the boardRows.concat near the bottom) —
 * just not its timing.
 *
 * The hero strip's live entries keep their own independent wave
 * (startLiveHeroWave, below) — the hero strip wasn't part of the "out of
 * sync" complaint, so its cadence (5200ms + its own reset dance) is
 * untouched.
 *
 * Only the data poll itself runs on its own separate interval.
 */
(function () {
  'use strict';
  if (!window.SATComponent) { return; }

  var LIVE_STATUS_URL = '/pages/sat-live-status';
  var POLL_MS = 6000;
  var MAX_LIVE = 5;
  var MAMMOTH_LAKES = 'MAMMOTH LAKES, CA';

  var lastSeenAt = null;
  var backfillLoaded = false;
  var liveOrders = []; // [{ city, code }], newest first

  var boardStep = 0, boardNoTransition = false;
  var heroStep = 0, heroNoTransition = false;

  // The bridge assets/sat-board-wave-patch.js's single wave loop uses to
  // drive this row on the same tick as the 6 fixed rows, instead of this
  // file running its own separate timer for it. `count` tells the loop
  // whether there's a live row to include in the sweep at all (and how
  // long its own stack is, since it can be shorter than the fixed rows'
  // always-5); `setStep` is the same shape as SATComponent's own
  // setRowStep(i, val, noTrans), minus the row index this file already
  // knows.
  window.SATLiveBoardBridge = {
    count: function () { return liveOrders.length; },
    setStep: function (step, noTrans) { boardStep = step; boardNoTransition = noTrans; }
  };

  function sleep(ms) { return new Promise(function (resolve) { setTimeout(resolve, ms); }); }

  function pushLiveCity(city) {
    var seq = pushLiveCity._seq || (pushLiveCity._seq = 9000);
    liveOrders.unshift({ city: city, code: String(seq) });
    pushLiveCity._seq = seq + 1;
    if (liveOrders.length > MAX_LIVE) liveOrders.length = MAX_LIVE;
  }

  // Hero strip: mirrors startHeroStrip's own per-step hold (5200ms) and its
  // post-reset snap-then-re-enable-transitions dance (380ms, then 70ms).
  async function startLiveHeroWave(self) {
    while (true) {
      if (!liveOrders.length) { await sleep(1000); continue; }
      var max = liveOrders.length;
      for (var step = 1; step <= max; step++) {
        await sleep(5200);
        heroStep = step;
        heroNoTransition = false;
        if (typeof self.forceUpdate === 'function') self.forceUpdate();
      }
      await sleep(380);
      heroStep = 0;
      heroNoTransition = true;
      if (typeof self.forceUpdate === 'function') self.forceUpdate();
      await sleep(70);
      heroNoTransition = false;
      if (typeof self.forceUpdate === 'function') self.forceUpdate();
    }
  }

  function poll(self) {
    fetch(LIVE_STATUS_URL, { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data) return;
        var changed = false;

        // One-time backfill from real historical orders, oldest first so the
        // most recent backfilled city still ends up nearest the front after
        // each unshift below.
        //
        // This used to also mark last_order_city/at as already-seen here, on
        // the assumption the backfill list's newest entry always is that
        // same order — true only at the single moment this feature was first
        // set up. custom.recent_order_cities is a one-time seed, never kept
        // in sync afterward, so on every real visit since then it's stale
        // relative to whatever order is actually most recent. Pre-marking
        // that order "seen" here silently hid it from every fresh page load
        // forever after — the live board never actually showed a visitor's
        // own just-placed order, only ever the frozen backfill cities.
        // Removed: the live-order check right below already handles a fresh
        // client correctly on its own (lastSeenAt starts null), so the
        // current order now surfaces the same way a genuinely new one
        // arriving mid-session already did.
        if (!backfillLoaded) {
          backfillLoaded = true;
          var recent = Array.isArray(data.recent) ? data.recent.slice().reverse() : [];
          for (var i = 0; i < recent.length; i++) pushLiveCity(recent[i]);
          if (recent.length) changed = true;
        }

        if (data.city && (lastSeenAt === null || data.at > lastSeenAt)) {
          lastSeenAt = data.at;
          pushLiveCity(data.city);
          changed = true;
        }

        if (changed && typeof self.forceUpdate === 'function') self.forceUpdate();
      })
      .catch(function () {});
  }

  var orig = window.SATComponent.prototype.renderVals;
  window.SATComponent.prototype.renderVals = function () {
    var out = orig.call(this);
    var self = this;

    if (!self.__satLivePollStarted) {
      self.__satLivePollStarted = true;
      poll(self);
      setInterval(function () { poll(self); }, POLL_MS);
    }

    if (!self.__satLiveHeroWaveStarted) {
      self.__satLiveHeroWaveStarted = true;
      startLiveHeroWave(self);
    }

    // ---- Hero strip: FLIGHT / ROUTING / LOAD / STATUS ----
    if (liveOrders.length && Array.isArray(out.heroStripCells) && out.heroStripCells.length === 4) {
      var liveByCol = [
        liveOrders.map(function (o) { return 'SAT‑' + o.code; }),
        liveOrders.map(function (o) { return String(o.city).toUpperCase() + ' → NEW ORDER'; }),
        liveOrders.map(function () { return '1 CRATE'; }),
        liveOrders.map(function () { return 'JUST IN'; })
      ];
      out.heroStripCells = out.heroStripCells.map(function (col, i) {
        var vals = liveByCol[i].concat(col.vals);
        return Object.assign({}, col, { vals: vals, stack: vals.concat([vals[0]]) });
      });
      out.heroStripTransform = 'translateY(' + (-heroStep * 19) + 'px)';
      out.heroStripTransition = heroNoTransition ? 'none' : 'transform 0.34s cubic-bezier(.3,1.4,.6,1)';
    }

    // ---- Departures flipboard: one extra, flipping row ----
    if (liveOrders.length && Array.isArray(out.boardRows)) {
      var cell = function (o) {
        return { city: o.city, code: o.code, status: 'NEW ORDER', color: '#F0C77A', remark: 'JUST IN' };
      };
      var stack = liveOrders.map(cell);
      stack.push(cell(liveOrders[0]));
      var reversedStack = stack.slice().reverse();
      var boardMax = reversedStack.length - 1;
      var liveRow = {
        cities: liveOrders.map(function (o) { return o.city; }),
        codes: liveOrders.map(function (o) { return o.code; }),
        stack: reversedStack,
        flipTransform: 'translateY(' + (-(boardMax - boardStep) * 19) + 'px)',
        // Matches assets/sat-board-wave-patch.js's smooth (non-bouncy)
        // easing on the base 6 rows, so the live row flips the same way
        // they do instead of standing out with the old overshoot curve.
        flipTransition: boardNoTransition ? 'none' : 'transform 0.48s cubic-bezier(.4,0,.2,1)',
        dotStyle: 'animation:pulseDot 1.8s ease-in-out infinite;'
      };
      out.boardRows = out.boardRows.concat([liveRow]);
    }

    // ---- Route Network: "Current Active Airways" ----
    if (liveOrders.length && Array.isArray(out.routeReadouts)) {
      var mammoth = out.routeReadouts.filter(function (r) { return r.city === MAMMOTH_LAKES; })[0];
      var others = out.routeReadouts.filter(function (r) { return r.city !== MAMMOTH_LAKES; });
      var liveReadouts = liveOrders.slice(0, 4).map(function (o) {
        return {
          code: 'SAT‑' + o.code,
          city: String(o.city).toUpperCase(),
          // rr.load is spliced straight into a CSS width (see snippets/sat-routes.liquid),
          // so it has to stay a valid width value — an em dash silently produced invalid
          // CSS and left the load bar blank for every live row.
          load: '0%',
          eta: 'LIVE',
          status: 'NEW ORDER',
          statusColor: '#2f7d4f'
        };
      });
      var merged = liveReadouts.concat(mammoth ? [mammoth] : []).concat(others);
      out.routeReadouts = merged.slice(0, 6);
      // Belt and suspenders: if Mammoth Lakes still somehow fell off the cap
      // (e.g. 5+ live entries), swap it back in over the last slot.
      if (mammoth && out.routeReadouts.indexOf(mammoth) === -1) {
        out.routeReadouts = out.routeReadouts.slice(0, 5).concat([mammoth]);
      }
    }

    return out;
  };
})();
